import { ReactNode } from "react";

export interface GetConfigTableColumn<T> {
  key: string;
  title: string;
  /** Optional className applied to every <td> of this column (e.g. "gateway-config__value--current"). */
  className?: string;
  /** Custom cell renderer. Falls back to reading `row[column.key]` when omitted. */
  render?: (row: T) => ReactNode;
}

interface GetConfigTableProps<T> {
  columns: GetConfigTableColumn<T>[];
  data: T[];
  emptyMessage?: string;
  getRowKey?: (row: T, index: number) => string | number;
  className?: string;
}

/**
 * Reusable table using the shared `.gateway-config__table` styling
 * (defined globally in `asset-configuration.scss`).
 *
 * Example:
 * <GetConfigTable
 *   columns={[
 *     { key: "parameterName", title: "Name" },
 *     { key: "previousValue", title: "Previous Value", className: "gateway-config__value--current" },
 *   ]}
 *   data={historyDetailsData}
 *   getRowKey={(row) => row.id}
 * />
 */
const GetConfigTable = <T,>({
  columns,
  data,
  emptyMessage = "No parameters returned from the device.",
  getRowKey,
  className,
}: GetConfigTableProps<T>) => {
  const wrapperClassName = ["gateway-config__table-wrap", className].filter(Boolean).join(" ");

  return (
    <div className={wrapperClassName}>
      <table className="gateway-config__table">
        <thead>
          <tr>
            {columns?.map((column) => (
              <th key={column?.key}>{column?.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.length > 0 ? (
            data?.map((row, index) => (
              <tr key={getRowKey ? getRowKey(row, index) : index}>
                {columns?.map((column) => (
                  <td key={column?.key} className={column?.className}>
                    {column?.render
                      ? column?.render(row)
                      : (((row as Record<string, unknown>)[column?.key] ?? "-") as ReactNode)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns?.length} className="gateway-config__empty">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GetConfigTable;
